import React from "react";
import { Link, useLocation } from "react-router-dom";
import { LogOut, Sun, Moon, User, Building2, Briefcase, Search, ChevronDown } from "lucide-react";
import Logo from "./Logo";

function getStoredUser() {
  try {
    const s = sessionStorage.getItem("wibil_user") || localStorage.getItem("wibil_user");
    return s && s !== "undefined" ? JSON.parse(s) : null;
  } catch (e) {
    return null;
  }
}

export default function Navbar({ theme, userName, onLogout, themeMode = "dark", setThemeMode }) {
  const location = useLocation();
  const user = getStoredUser();
  const userRole = (user?.role || "").toLowerCase().trim();

  const handleLogout = (e) => {
    if (e) e.preventDefault();
    sessionStorage.removeItem("wibil_token");
    sessionStorage.removeItem("wibil_user");
    sessionStorage.removeItem("wibil_session_started");
    localStorage.removeItem("wibil_token");
    localStorage.removeItem("wibil_user");
    localStorage.removeItem("wibil_session_started");
    if (typeof onLogout === "function") {
      onLogout();
    } else {
      window.location.href = `${(process.env.PUBLIC_URL || "").replace(/\/$/, "")}/login`;
    }
  };

  const isRole = (r) => {
    if (r === "super_admin") return userRole === "super_admin" || userRole === "super-admin";
    if (r === "job_seeker") return userRole === "job_seeker" || userRole === "jobseeker" || userRole === "candidate";
    if (r === "employer") return userRole === "employer" || userRole === "company";
    if (r === "recruiter") return userRole === "recruiter" || userRole === "hr";
    return userRole === "employee";
  };

  return (
    <nav className="navbar navbar-expand-lg bg-body-tertiary border-bottom sticky-top py-2 px-3">
      <div className="container-fluid px-0 px-md-3">
        
        {/* Brand Logo */}
        <Link to="/" className="navbar-brand text-decoration-none p-0 m-0 me-3">
          <Logo color="#0d6efd" size={20} subtitle={false} />
        </Link>

        {/* Center / Navigation Links for Active Sessions */}
        {user ? (
          <div className="d-none d-md-flex align-items-center gap-3 ms-2 me-auto">
            {isRole("super_admin") && (
              <Link to="/super-admin" className={`small fw-semibold text-decoration-none ${location.pathname === "/super-admin" ? "text-primary border-bottom border-primary border-2 pb-1" : "text-secondary"}`}>
                Super Admin
              </Link>
            )}

            {isRole("employer") && (
              <>
                <Link to="/employer" className={`small fw-semibold text-decoration-none ${location.pathname === "/employer" ? "text-primary border-bottom border-primary border-2 pb-1" : "text-secondary"}`}>
                  Employer Dashboard
                </Link>
                <Link to="/rate/employee" className={`small fw-semibold text-decoration-none ${location.pathname === "/rate/employee" ? "text-primary border-bottom border-primary border-2 pb-1" : "text-secondary"}`}>
                  Rate Employee
                </Link>
              </>
            )}

            {isRole("recruiter") && (
              <>
                <Link
                  to="/recruiter"
                  className={`small fw-semibold text-decoration-none ${location.pathname === "/recruiter" || location.pathname.startsWith("/recruiter/candidate-profile") ? "" : "text-secondary"}`}
                  style={{
                    padding: "6px 14px",
                    borderRadius: 8,
                    background: location.pathname === "/recruiter" || location.pathname.startsWith("/recruiter/candidate-profile") ? "#0d6efd" : "transparent",
                    color: location.pathname === "/recruiter" || location.pathname.startsWith("/recruiter/candidate-profile") ? "#fff" : undefined
                  }}
                >
                  Recruiter Hub
                </Link>
                <Link to="/opportunity-hub" className={`small fw-semibold text-decoration-none ${location.pathname === "/opportunity-hub" ? "text-primary border-bottom border-primary border-2 pb-1" : "text-secondary"}`}>
                  Social OSINT Analyzer
                </Link>
              </>
            )}

            {isRole("job_seeker") && (
              <>
                <Link to="/job-seeker" className={`small fw-semibold text-decoration-none ${location.pathname === "/job-seeker" ? "text-primary border-bottom border-primary border-2 pb-1" : "text-secondary"}`}>
                  Job Seeker Profile
                </Link>
                <Link to="/apply-for-job" className={`small fw-semibold text-decoration-none ${location.pathname === "/apply-for-job" ? "text-primary border-bottom border-primary border-2 pb-1" : "text-secondary"}`}>
                  Apply For Jobs
                </Link>
              </>
            )}

            {isRole("employee") && (
              <>
                <Link to="/employee" className={`small fw-semibold text-decoration-none ${location.pathname === "/employee" ? "text-primary border-bottom border-primary border-2 pb-1" : "text-secondary"}`}>
                  Employee Workspace
                </Link>
                <Link to="/rate/employer" className={`small fw-semibold text-decoration-none ${location.pathname === "/rate/employer" ? "text-primary border-bottom border-primary border-2 pb-1" : "text-secondary"}`}>
                  Rate Employer
                </Link>
              </>
            )}
          </div>
        ) : (
          /* Public Role Shortcuts */
          <div className="d-none d-lg-flex align-items-center gap-3 ms-2 me-auto">
            <Link to="/login/employee" className="small fw-semibold text-secondary text-decoration-none d-flex align-items-center gap-1">
              <User size={14} /> Employee
            </Link>
            <Link to="/login/employer" className="small fw-semibold text-secondary text-decoration-none d-flex align-items-center gap-1">
              <Building2 size={14} /> Employer
            </Link>
            <Link to="/login/job-seeker" className="small fw-semibold text-secondary text-decoration-none d-flex align-items-center gap-1">
              <Briefcase size={14} /> Job Seeker
            </Link>
            <Link to="/login/recruiter" className="small fw-semibold text-secondary text-decoration-none d-flex align-items-center gap-1">
              <Search size={14} /> Recruiter
            </Link>
          </div>
        )}

        {/* Right Controls: Theme Switcher & User Session */}
        <div className="d-flex align-items-center gap-2 gap-md-3 ms-auto">
          
          {/* Light / Dark Mode Toggle */}
          {setThemeMode && (
            <button
              type="button"
              onClick={() => setThemeMode(themeMode === "dark" ? "light" : "dark")}
              className="btn btn-sm btn-outline-secondary rounded-circle d-flex align-items-center justify-content-center p-0"
              title={`Switch to ${themeMode === "dark" ? "Light" : "Dark"} Mode`}
              style={{ width: 34, height: 34 }}
            >
              {themeMode === "dark" ? <Sun size={16} /> : <Moon size={16} />}
            </button>
          )}

          {/* User Account Info */}
          {userName || user?.name ? (
            <div className="d-flex align-items-center gap-2">
              <span className="d-none d-sm-inline small fw-semibold text-body">
                {userName || user?.name}
              </span>
              <button
                type="button"
                onClick={handleLogout}
                className="btn btn-sm btn-outline-danger d-flex align-items-center gap-1"
              >
                <LogOut size={13} />
                <span className="d-none d-sm-inline">Sign out</span>
              </button>
            </div>
          ) : (
            <div className="d-flex align-items-center gap-2">
              <Link to="/login" className="btn btn-primary btn-sm fw-bold">
                Sign In
              </Link>
              <div className="dropdown d-none d-sm-block">
                <button className="btn btn-outline-secondary btn-sm fw-bold dropdown-toggle d-flex align-items-center gap-1" type="button" data-bs-toggle="dropdown" aria-expanded="false">
                  Register <ChevronDown size={12} />
                </button>
                <ul className="dropdown-menu dropdown-menu-end shadow-sm">
                  <li><Link className="dropdown-item small" to="/register/employee">Register as Employee</Link></li>
                  <li><Link className="dropdown-item small" to="/register/employer">Register as Employer</Link></li>
                  <li><Link className="dropdown-item small" to="/register/job-seeker">Register as Job Seeker</Link></li>
                  <li><Link className="dropdown-item small" to="/register/recruiter">Register as Recruiter</Link></li>
                </ul>
              </div>
            </div>
          )}

        </div>

        {/* Mobile Portrait Nav Strip (Always visible on mobile portrait screens <768px) */}
        <div className="w-100 d-flex d-md-none align-items-center justify-content-start gap-2 pt-2 border-top border-secondary border-opacity-25 overflow-x-auto text-nowrap mt-2">
          {user ? (
            <>
              {isRole("employee") && (
                <Link to="/employee" className={`btn btn-sm ${location.pathname === "/employee" ? "btn-primary" : "btn-outline-secondary"} py-1 px-2 fw-semibold`} style={{ fontSize: 11 }}>
                  Workspace
                </Link>
              )}
              {isRole("employer") && (
                <Link to="/employer" className={`btn btn-sm ${location.pathname === "/employer" ? "btn-primary" : "btn-outline-secondary"} py-1 px-2 fw-semibold`} style={{ fontSize: 11 }}>
                  Employer
                </Link>
              )}
              {isRole("recruiter") && (
                <>
                  <Link to="/recruiter" className={`btn btn-sm ${location.pathname === "/recruiter" || location.pathname.startsWith("/recruiter/candidate-profile") ? "btn-primary" : "btn-outline-secondary"} py-1 px-3 fw-semibold`} style={{ fontSize: 12, borderRadius: 8 }}>
                    Recruiter Hub
                  </Link>
                  <Link to="/opportunity-hub" className={`btn btn-sm ${location.pathname === "/opportunity-hub" ? "btn-primary" : "btn-outline-secondary"} py-1 px-2 fw-semibold`} style={{ fontSize: 11 }}>
                    Social OSINT Analyzer
                  </Link>
                </>
              )}
              {isRole("job_seeker") && (
                <Link to="/job-seeker" className={`btn btn-sm ${location.pathname === "/job-seeker" ? "btn-primary" : "btn-outline-secondary"} py-1 px-2 fw-semibold`} style={{ fontSize: 11 }}>
                  Job Seeker
                </Link>
              )}
            </>
          ) : (
            <>
              <Link to="/login/employee" className="btn btn-sm btn-outline-secondary py-1 px-2 fw-semibold" style={{ fontSize: 11 }}>
                Employee
              </Link>
              <Link to="/login/employer" className="btn btn-sm btn-outline-secondary py-1 px-2 fw-semibold" style={{ fontSize: 11 }}>
                Employer
              </Link>
              <Link to="/login/job-seeker" className="btn btn-sm btn-outline-secondary py-1 px-2 fw-semibold" style={{ fontSize: 11 }}>
                Job Seeker
              </Link>
              <Link to="/login/recruiter" className="btn btn-sm btn-outline-secondary py-1 px-2 fw-semibold" style={{ fontSize: 11 }}>
                Recruiter
              </Link>
            </>
          )}
        </div>

      </div>
    </nav>
  );
}
